/**
 * Phase B1 — expanded Device Information display helpers.
 * Null/empty Fleet fields render as "Not reported" (never fabricated samples).
 */

export const NOT_REPORTED = "Not reported";

/** Bounded RTK Query cache retention for on-demand expand details (seconds). */
export const DEVICE_DETAIL_KEEP_UNUSED_DATA_SECONDS = 60;

/**
 * Expand must never poll, prefetch, or refetch on focus/reconnect.
 * setupListeners is enabled on the SA store; disable it per-query here.
 */
export const DEVICE_DETAIL_QUERY_OPTIONS = {
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  pollingInterval: 0,
} as const;

export type DeviceDetailFieldStatus =
  | "confirmed"
  | "stored"
  | "unavailable"
  | "unsupported";

export type DownloadSpeedTestSnapshot = {
  mbps?: number | null;
  testId?: string | null;
  measuredAt?: string | null;
  endpointHost?: string | null;
  durationMs?: number | null;
  bytesDownloaded?: number | null;
  confidence?: string | null;
  inFlight?: boolean;
  cooldownActive?: boolean;
};

export type DeviceDetailSection = {
  version?: string | null;
  heartbeat?: string | null;
  updated?: string | null;
  restarted?: string | null;
  wakeSleep?: string | null;
  wakeSleepEnabled?: boolean | null;
  startTime?: string | null;
  endTime?: string | null;
  /** T24 — confirmed weekend sleep. Absent/null = not reported by device. */
  sleepSaturday?: boolean | null;
  sleepSunday?: boolean | null;
  /**
   * T24 — Fleet-confirmed Music Scheduler snapshot (device-reported only).
   * Absent/null = not reported. Parsed defensively by confirmedMusicSchedule.ts.
   */
  musicSchedule?: unknown;
  volume?: string | number | null;
  masterLevel?: string | number | null;
  appLock?: string | boolean | null;
  appLockPinSet?: boolean | null;
  payment?: string | null;
  androidVersion?: string | null;
  timeZone?: string | null;
  timeZoneConfirmed?: boolean | null;
  rebooted?: string | null;
  lastIntentionalRebootAt?: string | null;
  lastRestartKind?: "intentional" | "unexpected" | "unknown" | null;
  rebootInFlight?: boolean | null;
  latLong?: string | null;
  serialNumber?: string | null;
  ethernetMac?: string | null;
  wifiMac?: string | null;
  wifiSsid?: string | null;
  wifiConnected?: boolean | null;
  activeTransport?: string | null;
  ethernet?: string | null;
  ipInternal?: string | null;
  ipWifi?: string | null;
  ipExternal?: string | null;
  download?: string | number | null;
  upload?: string | number | null;
  downloadTest?: DownloadSpeedTestSnapshot | null;
};

export type DeviceDetailPayload = {
  player: DeviceDetailSection;
  assistant: DeviceDetailSection;
  os: DeviceDetailSection;
  internet: DeviceDetailSection;
};

export type DeviceDetailFreshness = {
  capturedAt?: string | null;
  confirmedAppMetaSyncedAt?: string | null;
  confirmedMusicScheduleSyncedAt?: string | null;
  reportedDeviceInfoSyncedAt?: string | null;
  playerPackageSyncedAt?: string | null;
  assistantPackageSyncedAt?: string | null;
};

export type DeviceDetailResponse = {
  success?: boolean;
  deviceId: string;
  detail: DeviceDetailPayload;
  source?: string;
  freshness?: DeviceDetailFreshness;
  fieldStatus?: Record<string, DeviceDetailFieldStatus>;
  /** Explicit Fleet advertisement. Version is not proof of support. */
  supportsReportDeviceInfo?: boolean;
  capabilities?: { reportDeviceInfo?: boolean };
  commandSupport?: string[];
};

export function formatWifiConnectionStatus(
  connected: boolean | null | undefined
): string {
  if (connected === true) return "Connected";
  if (connected === false) return "Disconnected";
  return NOT_REPORTED;
}

export function formatWifiConnectionType(
  transport: string | null | undefined
): string {
  if (transport === "ethernet") return "Ethernet";
  if (transport === "wifi") return "Wi-Fi";
  if (transport === "other") return "Other";
  return NOT_REPORTED;
}

export function formatReportedValue(
  value: string | number | boolean | null | undefined
): string {
  if (value === null || value === undefined) return NOT_REPORTED;
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return NOT_REPORTED;
    return String(value);
  }
  if (typeof value !== "string") return NOT_REPORTED;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : NOT_REPORTED;
}

/** Match Assistant NetworkHealthUiMapper.formatMbps — one decimal, Mbps units. */
export function formatDownloadMbps(
  value: string | number | null | undefined
): string {
  if (value === null || value === undefined) return NOT_REPORTED;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0) return NOT_REPORTED;
  return `${n.toFixed(1)} Mbps`;
}

export function formatSpeedTestTimestamp(
  iso: string | null | undefined
): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  return new Date(then).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Heartbeat / Restarted / Rebooted display.
 * Null stays "Not reported". Valid ISO is locale date/time plus age.
 * The exact stored timestamp is available via healthTimestampExact (title).
 */
export function healthTimestampExact(
  value: string | null | undefined
): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ms = Date.parse(trimmed);
  return Number.isFinite(ms) ? trimmed : null;
}

export function formatHealthAge(
  ageMs: number
): string {
  if (!Number.isFinite(ageMs) || ageMs < 0) return "";
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatHealthTimestamp(
  value: string | number | boolean | null | undefined,
  nowMs: number = Date.now()
): string {
  if (typeof value !== "string") {
    return formatReportedValue(value);
  }
  const exact = healthTimestampExact(value);
  if (!exact) return NOT_REPORTED;
  const then = Date.parse(exact);
  const when = new Date(then).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
  const age = formatHealthAge(Math.max(0, nowMs - then));
  return age ? `${when} · ${age}` : when;
}

/** Volume / Master Level — keep the existing "75%" panel convention. */
export function formatPercentDisplay(
  value: string | number | null | undefined
): string {
  if (value === null || value === undefined) return NOT_REPORTED;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) {
    const raw = formatReportedValue(value);
    return raw === NOT_REPORTED ? NOT_REPORTED : raw;
  }
  return `${n}%`;
}

/**
 * App Lock is On/Off only. Never render a PIN, even if a payload leaks one.
 */
export function formatAppLockDisplay(
  appLock: string | boolean | null | undefined
): string {
  if (typeof appLock === "string") {
    const lower = appLock.trim().toLowerCase();
    if (/\bpin\b/i.test(lower) || /\d{4,}/.test(lower)) {
      if (lower === "on" || lower.startsWith("on ")) return "On";
      if (lower === "off" || lower.startsWith("off ")) return "Off";
      return NOT_REPORTED;
    }
  }
  return formatReportedValue(appLock);
}

export function formatSnapshotNote(response: {
  source?: string | null;
  freshness?: DeviceDetailFreshness | null;
} | null | undefined): string | null {
  if (!response) return null;
  const capturedAt = response.freshness?.capturedAt;
  const source = response.source;
  if (!capturedAt && !source) return null;
  const sourceLabel =
    source === "device_reported"
      ? "Confirmed device-reported values"
      : "Previously stored registry values";
  if (!capturedAt) return sourceLabel;
  const then = Date.parse(capturedAt);
  if (!Number.isFinite(then)) return sourceLabel;
  const when = new Date(then).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
  return `${sourceLabel} · as of ${when}`;
}

/**
 * Ignore stale responses when the expanded row's deviceId no longer matches.
 */
export function detailMatchesDevice(
  detailDeviceId: string | undefined,
  rowDeviceId: string
): boolean {
  if (!detailDeviceId || !rowDeviceId) return false;
  return detailDeviceId === rowDeviceId;
}

export function shouldFetchDeviceDetail(args: {
  expanded: boolean;
  deviceId?: string | null;
}): boolean {
  return Boolean(args.expanded && args.deviceId);
}

/** Cost scales with unique expanded rows, never with visible/collapsed count. */
export function estimateExpandDetailRequests(args: {
  visibleRowCount: number;
  expandedDeviceIds: Array<string | null | undefined>;
}): {
  detailRequests: number;
  neighboringRequests: number;
  collapsedRows: number;
} {
  const unique = new Set(
    args.expandedDeviceIds.filter((id): id is string => Boolean(id))
  );
  return {
    detailRequests: unique.size,
    neighboringRequests: 0,
    collapsedRows: Math.max(0, args.visibleRowCount - unique.size),
  };
}

/** User-facing expand-detail error copy for RTK Query / fetch failures. */
export function formatDeviceDetailError(
  error: unknown
): string {
  const status =
    error && typeof error === "object" && "status" in error
      ? (error as { status: unknown }).status
      : undefined;

  if (status === 404) {
    return `Device was not found in Fleet. Values below show ${NOT_REPORTED}.`;
  }
  if (status === 401 || status === 403) {
    return `Not authorized to load device information. Values below show ${NOT_REPORTED}.`;
  }
  if (status === "FETCH_ERROR" || status === undefined) {
    return `Device information could not be loaded (offline or unreachable). Values below show ${NOT_REPORTED}.`;
  }
  return `Device information could not be loaded. Values below show ${NOT_REPORTED}.`;
}
